import { isValidArPhone } from "./arPhone";

// Las mismas reglas que la barrera de la API (`order-address-readiness.util.ts` en
// xcon-api): lo que pasa acá tiene que pasar allá, o el operador ve el error recién
// al confirmar.
// Los modales de entrega y facturación las usan también, para marcar el campo al guardar.
export const FILLER_VALUES = new Set(["-", "0", "00", "000", "00000000"]);
const digitsOf = (v: unknown) => String(v ?? "").replace(/\D+/g, "");
export const isValidDni = (v: unknown) => /^[1-9][0-9]{6,7}$/.test(digitsOf(v));
export const isValidCuit = (v: unknown) => /^[0-9]{11}$/.test(digitsOf(v));
/** El valor, o "" si es un relleno de Magento: un "-" no se precarga como dato. */
export const realValue = (v: unknown) => {
  const t = String(v ?? "").trim();
  return FILLER_VALUES.has(t) ? "" : t;
};

/**
 * Pure validation helpers for the quote "Confirmar pedido" flow.
 *
 * Two layers:
 *   - Quote-level (owner, shipping, billing) — one set of errors per quote.
 *   - Seller-level (stock, shipping per DG, payment) — one set per seller.
 *
 * Errors return the string keys registered in i18n (quotes.validation.*),
 * not literal copy, so the consumer can `t(key)` them.
 */

export interface QuoteLevelErrors {
  owner?: string; // i18n key
  shipping?: string;
  billing?: string;
}

export interface SellerLevelErrors {
  outOfStockItemIds: number[]; // empty = no stock errors
  /**
   * Ítems con stock > 0 pero MENOS que la cantidad pedida, y sin reposición.
   * Se lleva `stock` y `qty` para poder decir el número en el mensaje.
   */
  insufficientStockItems: Array<{ itemId: number; stock: number; qty: number }>;
  dgsWithoutShipping: number[]; // DG ids missing a selected rate
  paymentMissing: boolean; // no method selected
  paymentIncomplete: boolean; // firstdata selected but card/installment/cardholder missing
}

export interface QuoteLevelInput {
  owner: { contactId?: number; userId?: number | null } | null;
  shippingAddr: Record<string, any> | null | undefined;
  billingAddr: Record<string, any> | null | undefined;
}

export function validateQuoteLevel(input: QuoteLevelInput): QuoteLevelErrors {
  const errors: QuoteLevelErrors = {};

  if (!input.owner || !input.owner.contactId) {
    errors.owner = "quotes.validation.owner.assign";
  }

  if (!isShippingAddressComplete(input.shippingAddr)) {
    errors.shipping = "quotes.validation.shipping.address.incomplete";
  }

  if (!isBillingAddressComplete(input.billingAddr)) {
    errors.billing = "quotes.validation.billing.incomplete";
  }

  return errors;
}

export function hasQuoteLevelErrors(errors: QuoteLevelErrors): boolean {
  return !!(errors.owner || errors.shipping || errors.billing);
}

/** Required fields on shipping snapshot (parity with DeliveryAddressModal rules). */
function isShippingAddressComplete(
  addr: Record<string, any> | null | undefined,
): boolean {
  if (!addr) return false;
  const required = [
    ["firstname", "name"],
    ["street1", "address"],
    ["phone"],
    ["cityName", "city"],
    ["vatId", "recipientId"],
    ["provinceName", "state"],
    ["postcode", "zipCode"],
  ];
  const filled = required.every((aliases) =>
    aliases.some((key) => {
      const v = addr[key];
      return (
        v !== undefined &&
        v !== null &&
        String(v).trim().length > 0 &&
        !FILLER_VALUES.has(String(v).trim())
      );
    }),
  );
  // Cargado no alcanza: una dirección vieja de la agenda con el teléfono cortado
  // ("011 3144-05") contaba como completa y el pedido se confirmaba igual. Lo mismo
  // el DNI: la API rechaza el pedido si no tiene 7 u 8 dígitos (#378).
  return (
    filled &&
    isValidArPhone(addr.phone) &&
    isValidDni(addr.vatId ?? addr.recipientId)
  );
}


/** Required fields on billing snapshot (parity with BillingDataModal rules). */
function isBillingAddressComplete(
  addr: Record<string, any> | null | undefined,
): boolean {
  if (!addr) return false;

  const billingType = addr.billingType ? String(addr.billingType) : "";
  if (!billingType) return false;

  // Types 2, 3, 4 are company invoices and require full postal address;
  // types 1 and 5 (consumidor final) only need name + ID.
  const COMPANY_TYPES = ["2", "3", "4"];
  const isCompany = COMPANY_TYPES.includes(billingType);

  const required: string[][] = [
    ["firstname", "fullName", "company"],
    ["vatId", "taxId", "documentId"],
  ];

  if (isCompany) {
    required.push(
      ["street1", "taxDirectorate"],
      ["cityName", "city"],
      ["provinceName", "state"],
      ["postcode", "zipCode"],
    );
  }

  const filled = required.every((aliases) =>
    aliases.some((key) => {
      const v = addr[key];
      return (
        v !== undefined &&
        v !== null &&
        String(v).trim().length > 0 &&
        !FILLER_VALUES.has(String(v).trim())
      );
    }),
  );
  const vatId = addr.vatId ?? addr.taxId ?? addr.documentId;
  return filled && (isCompany ? isValidCuit(vatId) : isValidDni(vatId));
}

export interface SellerLevelInput {
  /** Prices array returned by GET .../sellers/:code/prices */
  prices: Array<any>;
  /** Shipping object from paymentMethods/shipping state — currentSelection per DG */
  shippingByDg: Record<
    number,
    { selected?: string; rate?: string } | null | undefined
  >;
  /** Active delivery groups in this seller (deduped from items) */
  activeDeliveryGroups: number[];
  /** paymentMethods object from GET .../payment/methods, incl. currentSelection */
  paymentMethods: any | null;
  /** Selected card type (FirstData) — from local state */
  selectedCard: string | null | undefined;
}

export function validateSellerLevel(input: SellerLevelInput): SellerLevelErrors {
  // Stock: sólo bloquea cuando NO hay reposición (leadtime 0/ausente). Con leadtime
  // el faltante se repone, así que se puede vender por encima del stock — eso ya lo
  // avisa la columna Stock ("+ Stock en N días") y no debe frenar la compra.
  //
  // 🔴 Sin reposición hay DOS casos, y antes sólo se miraba el primero: la regla era
  // `stock === 0` y no comparaba contra la cantidad pedida, así que un ítem con 24 de
  // stock dejaba confirmar un pedido de 100. Reportado por el equipo el 2026-08-10.
  const outOfStockItemIds: number[] = [];
  const insufficientStockItems: Array<{
    itemId: number;
    stock: number;
    qty: number;
  }> = [];
  for (const item of input.prices || []) {
    const stock = Number(item.stock);
    const leadtime = Number(item.leadtime);
    const qty = Number(item.qty) || 1;
    // Reposición en 0 **o negativo** = sin reposición (misma regla que el label de
    // la columna Stock, ver utils/stockLabel.ts).
    const sinReposicion = !Number.isFinite(leadtime) || leadtime <= 0;

    if (!Number.isFinite(stock) || !sinReposicion || item.itemId == null) continue;

    if (stock <= 0) {
      outOfStockItemIds.push(item.itemId);
    } else if (qty > stock) {
      insufficientStockItems.push({ itemId: item.itemId, stock, qty });
    }
  }

  // Shipping: every active DG must have a rate selected.
  // Note: storepickup persists as { rate: "storepickup", selected: "0" } — rateId
  // is 0, so we can't use `selected` to detect "no selection". Use `rate` instead,
  // which is only present when a rate has been selected for that DG.
  const dgsWithoutShipping: number[] = [];
  for (const dg of input.activeDeliveryGroups) {
    const sel = input.shippingByDg?.[dg];
    const rate = sel?.rate;
    if (!rate || String(rate).trim() === "") {
      dgsWithoutShipping.push(dg);
    }
  }

  // Payment: must have a method. If FirstData, must have card + installment + cardholder.
  const cs = input.paymentMethods?.currentSelection || null;
  const method = cs?.payment_method;
  const paymentMissing = !method;

  let paymentIncomplete = false;
  if (method === "firstdata") {
    const card = input.selectedCard || cs?.card || "";
    const installment = cs?.fd || "";
    const name = cs?.name || "";
    const cardId = cs?.card_id || "";
    if (!card || !installment || !name || !cardId) {
      paymentIncomplete = true;
    }
  }

  return {
    outOfStockItemIds,
    insufficientStockItems,
    dgsWithoutShipping,
    paymentMissing,
    paymentIncomplete,
  };
}

export function hasSellerLevelErrors(errors: SellerLevelErrors): boolean {
  return (
    errors.outOfStockItemIds.length > 0 ||
    errors.insufficientStockItems.length > 0 ||
    errors.dgsWithoutShipping.length > 0 ||
    errors.paymentMissing ||
    errors.paymentIncomplete
  );
}
